<template>
  <v-container>
    <h3>グラフ表示設定</h3>

    <v-row class="mt-8 mb-5">

      <v-col cols="12" v-for="(chartArea, index) in chartAreas" v-bind:key="chartArea.key">
        <v-card variant="outlined" elevation="1">
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-label class="text-subtitle-2">縦軸</v-label>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 3">
                    <span class="ms-1">左</span>
                    <v-btn variant="outlined" prepend-icon="mdi-wrench" size="small" class="ms-12 mt-n1"
                      v-on:click="showYAxisDialog(index, false);">変更</v-btn>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 4">
                    <v-label class="ms-2 text-subtitle-2">最小値</v-label>
                    <span class="ms-5 text-subtitle-1">{{ chartArea.yAxis.left.min }}</span>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 4">
                    <v-label class="ms-2 text-subtitle-2">最大値</v-label>
                    <span class="ms-5 text-subtitle-1">{{ chartArea.yAxis.left.max }}</span>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 3">
                    <span class="ms-1">右</span>
                    <v-btn variant="outlined" prepend-icon="mdi-wrench" size="small" class="ms-12 mt-n1"
                      v-on:click="showYAxisDialog(index, true);">変更</v-btn>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 4" v-if="chartArea.yAxis.right.isSameLeft">
                    <span class="ms-2">左と同一</span>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 4" v-if="!chartArea.yAxis.right.isSameLeft">
                    <v-label class="ms-2 text-subtitle-2">最小値</v-label>
                    <span class="ms-5 text-subtitle-1">{{ chartArea.yAxis.right.min }}</span>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 4" v-if="!chartArea.yAxis.right.isSameLeft">
                    <v-label class="ms-2 text-subtitle-2">最大値</v-label>
                    <span class="ms-5 text-subtitle-1">{{ chartArea.yAxis.right.max }}</span>
                  </v-col>
                </v-row>
              </v-col>

              <v-divider class="mt-3 mb-2"></v-divider>

              <v-col cols="12">
                <v-label class="text-subtitle-2">表示チャンネル</v-label>
                <p class="ms-1 mt-2" v-if="chartArea.channels.length == 0">なし</p>
              </v-col>

              <v-col cols="12" v-for="(channel, chIndex) in chartArea.channels" v-bind:key="channel.key">
                <v-row>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 10">
                    <v-row>
                      <v-col cols="12">
                        <span class="ms-1">{{ chIndex + 1 }}.</span>
                      </v-col>

                      <v-col cols="12">
                        <v-row>
                          <v-col cols="5" class="d-flex align-center">
                            <v-label class="ms-2 text-subtitle-2">モジュール</v-label>
                          </v-col>
                          <v-col cols="7">
                            <span class="text-subtitle-1">{{ channel.moduleID }}</span>
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col cols="12">
                        <v-label class="ms-2 text-subtitle-2">チャンネル</v-label>
                      </v-col>
                      <v-col cols="12">
                        <span class="ms-3 text-subtitle-1">
                          {{ (channel.moduleID in channelsMap) ? channelsMap[channel.moduleID].channels[channel.ch].name : '' }}
                        </span>
                      </v-col>

                      <v-col cols="12">
                        <v-row>
                          <v-col cols="5" class="d-flex align-center">
                            <v-label class="ms-2 text-subtitle-2">基準縦軸</v-label>
                          </v-col>
                          <v-col cols="7">
                            <v-select density="compact" hide-details="true"
                              v-model:model-value="channel.scaleYAxis" @update:model-value="saveToLocalStorage"
                              :items="[ { title: '左', value: 'left' }, { title: '右', value: 'right' } ]"
                              :disabled="chartArea.yAxis.right.isSameLeft"></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 2" class="d-flex align-center"
                    v-bind:class="{ 'justify-end': $vuetify.display.mobile, 'justify-space-around': !$vuetify.display.mobile }">
                    <v-btn variant="text" color="error" v-on:click="deleteChannel(index, chIndex);">削除</v-btn>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" class="mt-4" v-if="chartArea.channels.length < 4">
                <v-btn variant="outlined" density="comfortable" v-on:click="showChannelDialog(index);">追加する</v-btn>
              </v-col>

              <v-divider class="my-2"></v-divider>

              <v-col cols="12">
                <v-label class="text-subtitle-2">基準線</v-label>
                <p class="ms-1 mt-2" v-if="chartArea.referenceLines.length == 0">なし</p>
              </v-col>

              <v-col cols="12" v-for="(referenceLine, refLineIndex) in chartArea.referenceLines" v-bind:key="referenceLine.key">
                <v-row>
                  <v-col :cols="$vuetify.display.mobile ? 12 : 1" class="d-flex align-center">
                    <span class="ms-1">{{ refLineIndex + 1 }}.</span>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
                    <v-row>
                      <v-col cols="5" class="d-flex align-center">
                        <v-label class="ms-2 text-subtitle-2">表示位置</v-label>
                      </v-col>
                      <v-col cols="7">
                        <span class="text-subtitle-1">{{ referenceLine.position }}</span>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 5">
                    <v-row>
                      <v-col cols="5" class="d-flex align-center">
                        <v-label class="ms-2 text-subtitle-2">基準縦軸</v-label>
                      </v-col>
                      <v-col cols="7">
                        <v-select density="compact" hide-details="true"
                          v-model:model-value="referenceLine.scaleYAxis" @update:model-value="saveToLocalStorage"
                          :items="[ { title: '左', value: 'left' }, { title: '右', value: 'right' } ]"
                          :disabled="chartArea.yAxis.right.isSameLeft"></v-select>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col :cols="$vuetify.display.mobile ? 12 : 2" class="d-flex align-center"
                    v-bind:class="{ 'justify-end': $vuetify.display.mobile, 'justify-space-around': !$vuetify.display.mobile }">
                    <v-btn variant="text" color="error" v-on:click="deleteReferenceLine(index, refLineIndex);">削除</v-btn>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" class="mt-4" v-if="chartArea.referenceLines.length < 4">
                <v-btn variant="outlined" density="comfortable" v-on:click="showReferenceLineDialog(index);">追加する</v-btn>
              </v-col>

              <v-divider class="mt-2"></v-divider>

            </v-row>
          </v-card-text>

          <v-card-actions class="d-flex justify-space-between">
            <div>
              <v-btn icon="mdi-arrow-up" size="small"
                v-on:click="upDisplayOrder(index);" v-bind:class="{ 'v-hidden': index === 0 }"></v-btn>
              <v-btn icon="mdi-arrow-down" size="small"
                v-on:click="downDisplayOrder(index);" v-bind:class="{ 'v-hidden': index === (chartAreas.length - 1) }"></v-btn>
            </div>

            <v-btn icon="mdi-delete" size="small" color="error" v-on:click="deleteChartArea(index);"></v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" class="d-flex justify-center" v-if="chartAreas.length < 8">
        <v-btn elevation="1" prepend-icon="mdi-plus" v-on:click="addNewChartArea">
          グラフを追加
        </v-btn>
      </v-col>

    </v-row>

    <v-dialog persistent max-width="800" v-model="isShowYAxisDialog">
      <v-card height="100dvh">
        <v-card-item>
          <v-card-title>
            縦軸変更 - {{ isEditingRightYAxis ? '右' : '左' }}
          </v-card-title>
        </v-card-item>

        <v-card-text class="mt-3">
          <v-form @submit.prevent ref="yAxisDialogForm">
            <v-row>
              <v-col cols="12" v-if="isEditingRightYAxis">
                <v-switch inset color="primary" hide-details="true" v-model="isSameLeft">
                  <template v-slot:label>左と同一</template>
                </v-switch>
              </v-col>

              <v-col cols="12">
                <v-label class="mb-3">最小値</v-label>
                <v-text-field density="compact"
                  :rules="[ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ]"
                  persistent-hint hint="必須、数値"
                  v-model="min" :readonly="isSameLeft"></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-label class="mb-3">最大値</v-label>
                <v-text-field density="compact"
                  :rules="[ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ]"
                  persistent-hint hint="必須、数値"
                  v-model="max" :readonly="isSameLeft"></v-text-field>
              </v-col>

              <v-col cols="12" v-if="isShowMinValueLargeErrorMessage">
                <v-alert variant="tonal" type="error" v-model="isShowMinValueLargeErrorMessage" closable>
                  最大値は最小値よりも大きい値にしてください
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 d-flex justify-end">
          <v-btn variant="outlined" v-on:click="isShowYAxisDialog = false;">
            キャンセル
          </v-btn>
          <v-btn variant="flat" color="primary" v-on:click="updateYAxis">
            変更
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent max-width="800" v-model="isShowChannelDialog">
      <v-card height="100dvh">
        <v-card-item>
          <v-card-title>
            表示チャンネル追加
          </v-card-title>
        </v-card-item>

        <v-card-text class="mt-3">
          <v-form @submit.prevent ref="channelDialogForm">
            <v-row>
              <v-col :cols="$vuetify.display.mobile ? 12 : 5">
                <v-label class="mb-3">モジュール選択</v-label>
                <v-select density="compact"
                  :rules="[ v => (v !== '') || '必須' ]"
                  persistent-hint hint="必須"
                  v-model:model-value="moduleID" @update:model-value="setChannels"
                  :items="browsableModules"></v-select>
              </v-col>

              <v-col cols="12">
                <v-label class="mb-3">チャンネル選択</v-label>
                <v-select density="compact"
                  :rules="[ v => (v !== '') || '必須' ]"
                  persistent-hint hint="必須"
                  v-model:model-value="channelKey"
                  :items="channels"></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 d-flex justify-end">
          <v-btn variant="outlined" v-on:click="isShowChannelDialog = false;">
            キャンセル
          </v-btn>
          <v-btn variant="flat" color="primary" v-on:click="addChannel">
            追加
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent max-width="800" v-model="isShowReferenceLineDialog">
      <v-card height="100dvh">
        <v-card-item>
          <v-card-title>
            基準線追加
          </v-card-title>
        </v-card-item>

        <v-card-text class="mt-3">
          <v-form @submit.prevent ref="referenceLineDialogForm">
            <v-row>
              <v-col cols="12">
                <v-label class="mb-3">表示位置</v-label>
                <v-text-field density="compact"
                  :rules="[ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ]"
                  persistent-hint hint="必須、数値"
                  v-model="position"></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 d-flex justify-end">
          <v-btn variant="outlined" v-on:click="isShowReferenceLineDialog = false;">
            キャンセル
          </v-btn>
          <v-btn variant="flat" color="primary" v-on:click="addReferenceLine">
            追加
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';

import axios from 'axios';

import config from '@/config.js';

export default defineComponent({
  name: 'ChartViewSetting',

  setup() {

    const browsableModules = ref([
      { title: '未選択', value: '' }
    ]);

    const channelsMap = ref({});


    const chartAreas = ref([]);

    const editingIndex = ref(-1);


    const isShowYAxisDialog = ref(false);
    const isEditingRightYAxis = ref(false);

    const yAxisDialogForm = ref();
    const isSameLeft = ref(false);
    const min = ref('');
    const max = ref('');

    const isShowMinValueLargeErrorMessage = ref(false);


    const isShowChannelDialog = ref(false);

    const channelDialogForm = ref();
    const moduleID = ref('');
    const channelKey = ref('');
    const channels = ref([
      { title: '未選択', value: '' }
    ]);


    const isShowReferenceLineDialog = ref(false);

    const referenceLineDialogForm = ref();
    const position = ref('');


    onMounted(() => {

      const chartAreasSetting = window.localStorage.getItem('chart-areas-setting');
      if (chartAreasSetting != null) {
        chartAreas.value = JSON.parse(chartAreasSetting);
      }


      watch(isSameLeft, () => {
        if (isSameLeft.value) {
          restoreLeftYAxisValue();
        }
      });


      fetchAllBrowsableChannels();

    });

    const fetchAllBrowsableChannels = () => {

      axios.get(
        config.API_URL +
        `/users/me`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
      ).then((response) => {

        response.data.browsableModules.forEach((moduleID) => {

          browsableModules.value.push({
            title: moduleID,
            value: moduleID
          });

          fetchChannelList(moduleID);

        });

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    const fetchChannelList = (moduleID) => {

      axios.get(
        config.API_URL +
        `/modules/${ moduleID }/channels`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((response) => {

        channelsMap.value[moduleID] = {
          channels: {}
        };

        for (let key in response.data.channels) {

          let generalName;

          if (key.startsWith('a')) {
            generalName = 'アナログ' + key.substring(1);
          }
          else if (key.startsWith('do')) {
            generalName = 'デジタル出力' + key.substring(2);
          }
          else {
            generalName = 'デジタル入力' + key.substring(1);
          }

          generalName = '（' + generalName + '）';

          channelsMap.value[moduleID].channels[key] = {
            name: generalName + response.data.channels[key].name
          };

        }

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };


    const addNewChartArea = () => {

      chartAreas.value.push({
        key: crypto.randomUUID(),

        yAxis: {
          left: {
            min: 0,
            max: 100
          },
          right: {
            isSameLeft: true,

            min: 0,
            max: 100
          }
        },

        channels: [],

        referenceLines: []
      });

      saveToLocalStorage();

    };


    const showYAxisDialog = (index, isRight) => {

      editingIndex.value = index;

      isEditingRightYAxis.value = isRight;

      if (isRight) {

        isSameLeft.value = chartAreas.value[index].yAxis.right.isSameLeft;

        min.value = chartAreas.value[index].yAxis.right.min.toString();
        max.value = chartAreas.value[index].yAxis.right.max.toString();

      } else {

        isSameLeft.value = false;

        min.value = chartAreas.value[index].yAxis.left.min.toString();
        max.value = chartAreas.value[index].yAxis.left.max.toString();

      }

      isShowMinValueLargeErrorMessage.value = false;

      isShowYAxisDialog.value = true;

    };

    const restoreLeftYAxisValue = () => {

      min.value = chartAreas.value[editingIndex.value].yAxis.left.min.toString();
      max.value = chartAreas.value[editingIndex.value].yAxis.left.max.toString();

    };

    const updateYAxis = async () => {

      const { valid } = await yAxisDialogForm.value.validate();
      if (!valid) {
        return;
      }


      if (Number(min.value) >= Number(max.value)) {
        isShowMinValueLargeErrorMessage.value = true;
        return;
      }


      if (isEditingRightYAxis.value) {

        chartAreas.value[editingIndex.value].yAxis.right.min = Number(min.value);
        chartAreas.value[editingIndex.value].yAxis.right.max = Number(max.value);

      } else {

        chartAreas.value[editingIndex.value].yAxis.left.min = Number(min.value);
        chartAreas.value[editingIndex.value].yAxis.left.max = Number(max.value);

        if (chartAreas.value[editingIndex.value].yAxis.right.isSameLeft) {
          chartAreas.value[editingIndex.value].yAxis.right.min = Number(min.value);
          chartAreas.value[editingIndex.value].yAxis.right.max = Number(max.value);
        }

      }

      chartAreas.value[editingIndex.value].yAxis.right.isSameLeft = (
        chartAreas.value[editingIndex.value].yAxis.left.min == chartAreas.value[editingIndex.value].yAxis.right.min &&
        chartAreas.value[editingIndex.value].yAxis.left.max == chartAreas.value[editingIndex.value].yAxis.right.max
      );

      if (chartAreas.value[editingIndex.value].yAxis.right.isSameLeft) {

        chartAreas.value[editingIndex.value].channels.forEach((channel) => {
          channel.scaleYAxis = 'left';
        });

        chartAreas.value[editingIndex.value].referenceLines.forEach((refLine) => {
          refLine.scaleYAxis = 'left';
        });

      }

      saveToLocalStorage();


      isShowYAxisDialog.value = false;

    };


    const showChannelDialog = (index) => {

      editingIndex.value = index;

      moduleID.value = '';

      setChannels();

      isShowChannelDialog.value = true;

    };

    const setChannels = () => {

      channelKey.value = '';

      channels.value = [
        { title: '未選択', value: '' }
      ];

      if (moduleID.value !== '') {
        for (let key in channelsMap.value[moduleID.value].channels) {

          channels.value.push({
            title: channelsMap.value[moduleID.value].channels[key].name,
            value: key
          });

        }
      }

    };

    const addChannel = async () => {

      const { valid } = await channelDialogForm.value.validate();
      if (!valid) {
        return;
      }

      chartAreas.value[editingIndex.value].channels.push({
        key: crypto.randomUUID(),

        moduleID: moduleID.value,
        ch: channelKey.value,

        scaleYAxis: 'left'
      });

      saveToLocalStorage();

      isShowChannelDialog.value = false;

    };

    const deleteChannel = (index, chIndex) => {

      chartAreas.value[index].channels.splice(chIndex, 1);

      saveToLocalStorage();

    };


    const showReferenceLineDialog = (index) => {

      editingIndex.value = index;

      position.value = '';

      isShowReferenceLineDialog.value = true;

    };

    const addReferenceLine = async () => {

      const { valid } = await referenceLineDialogForm.value.validate();
      if (!valid) {
        return;
      }

      chartAreas.value[editingIndex.value].referenceLines.push({
        key: crypto.randomUUID(),

        position: Number(position.value),

        scaleYAxis: 'left'
      });

      saveToLocalStorage();

      isShowReferenceLineDialog.value = false;

    };

    const deleteReferenceLine = (index, refLineIndex) => {

      chartAreas.value[index].referenceLines.splice(refLineIndex, 1);

      saveToLocalStorage();

    };


    const upDisplayOrder = (index) => {

      const tmp = chartAreas.value[index - 1];
      chartAreas.value[index - 1] = chartAreas.value[index];
      chartAreas.value[index] = tmp;

      saveToLocalStorage();

    };

    const downDisplayOrder = (index) => {

      const tmp = chartAreas.value[index + 1];
      chartAreas.value[index + 1] = chartAreas.value[index];
      chartAreas.value[index] = tmp;

      saveToLocalStorage();

    };

    const deleteChartArea = (index) => {

      chartAreas.value.splice(index, 1);

      saveToLocalStorage();

    };


    const saveToLocalStorage = () => {

      window.localStorage.setItem('chart-areas-setting', JSON.stringify(chartAreas.value));

    };


    return {
      browsableModules,
      channelsMap,

      chartAreas,

      editingIndex,


      isShowYAxisDialog,
      isEditingRightYAxis,

      yAxisDialogForm,
      isSameLeft,
      min,
      max,

      isShowMinValueLargeErrorMessage,


      isShowChannelDialog,

      channelDialogForm,
      moduleID,
      channelKey,
      channels,


      isShowReferenceLineDialog,

      referenceLineDialogForm,
      position,


      addNewChartArea,

      showYAxisDialog,
      updateYAxis,

      showChannelDialog,
      setChannels,
      addChannel,
      deleteChannel,

      showReferenceLineDialog,
      addReferenceLine,
      deleteReferenceLine,

      upDisplayOrder,
      downDisplayOrder,

      deleteChartArea,

      saveToLocalStorage
    }

  }
});
</script>
